import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AtSign, Hash, QrCode, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { api, errorMessage } from "@/lib/api";

export default function ChatNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("code") ? "code" : "username");
  const [value, setValue] = useState(params.get("code") || "");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (v = value) => {
    const q = mode === "username" ? { username: v.replace(/^@/, "").toLowerCase() } : { code: v.toUpperCase() };
    setBusy(true); setFound(null);
    try { const { data } = await api.get("/chat/lookup", { params: q }); setFound(data); }
    catch (e) { toast.error(e?.response?.status === 404 ? "Usuário não encontrado." : errorMessage(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (params.get("code")) lookup(params.get("code")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async () => {
    setBusy(true);
    try {
      const body = mode === "username" ? { username: found.username } : { code: value.toUpperCase() };
      const { data } = await api.post("/chat/requests", body);
      if (data.status === "accepted") { toast.success("Conversa liberada!"); navigate("/chat"); } else { toast.success(`Solicitação enviada para @${found.username}. A conversa abre quando a pessoa aceitar.`); navigate("/chat"); }
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="ga-fade-up pb-10" data-testid="chat-new-page">
      <PageHeader title="Novo contato" subtitle="Peça permissão antes de conversar" />
      <div className="space-y-4 px-5">
        <div className="flex gap-2">
          {[["username", AtSign, "@nome"], ["code", Hash, "Código"]].map(([m, Icon, label]) => (
            <button key={m} onClick={() => { setMode(m); setFound(null); setValue(""); }} className="flex flex-1 items-center justify-center gap-1 rounded-full py-2 text-sm font-medium transition-colors" style={{ background: mode === m ? "#13C9A3" : "var(--ga-surface)", color: mode === m ? "#04332a" : "var(--ga-text)" }} data-testid={`chat-new-mode-${m}`}><Icon size={14} /> {label}</button>
          ))}
        </div>
        <div className="ga-card p-4">
          <div className="flex gap-2">
            <input value={value} onChange={(e) => setValue(mode === "code" ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) : e.target.value.toLowerCase().replace(/[^a-z0-9_.@]/g, ""))} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder={mode === "username" ? "@nome.exato" : "CÓDIGO DE 8 LETRAS"} className="ga-input flex-1" data-testid="chat-new-input" />
            <button onClick={() => lookup()} disabled={busy || value.length < 3} className="ga-btn-primary px-4" aria-label="Buscar" data-testid="chat-new-search"><Search size={18} /></button>
          </div>
          <p className="ga-muted mt-2 text-xs">{mode === "username" ? "Só funciona com o @nome exato — não há lista nem busca parcial de usuários." : "Peça o código ou leia o QR Code da pessoa com a câmera do celular."}</p>
          {found && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl p-3" style={{ background: "var(--ga-surface)" }} data-testid="chat-new-found">
              <div className="flex h-11 w-11 items-center justify-center rounded-full font-bold text-white" style={{ background: "#4E78FF" }}>{found.display_name[0]?.toUpperCase()}</div>
              <div className="min-w-0 flex-1"><div className="truncate font-semibold">{found.display_name}</div><div className="ga-muted text-xs">@{found.username}</div></div>
              <button onClick={request} disabled={busy} className="ga-btn-primary px-3 py-2 text-sm" data-testid="chat-new-request"><Send size={14} /> Solicitar</button>
            </div>
          )}
        </div>
        <button onClick={() => navigate("/chat/ajustes")} className="ga-btn-ghost w-full text-sm" data-testid="chat-new-my-qr"><QrCode size={16} /> Mostrar meu QR Code e código</button>
      </div>
    </div>
  );
}
